import React, { useEffect, useState } from 'react';
import { DropDown, Flex, TextField } from '@elementary/common-ui/components';

type FileSizeUnit = 'KB' | 'MB' | 'GB';

const FILE_SIZE_UNIT_OPTIONS = [
  { value: 'KB', label: 'KB' },
  { value: 'MB', label: 'MB' },
  { value: 'GB', label: 'GB' },
] as const;

const FILE_SIZE_REGEX = /^\s*(\d+(?:\.\d+)?)\s*(KB|MB|GB)\s*$/i;
const AMOUNT_ONLY_REGEX = /^\s*\d+(?:\.\d+)?\s*$/;

const splitMaxFileSize = (
  value: string | undefined,
): { amount: string; unit: FileSizeUnit | undefined } => {
  const trimmed = (value || '').trim();
  const match = trimmed.match(FILE_SIZE_REGEX);
  if (!match) {
    if (!trimmed) {
      return { amount: '', unit: undefined };
    }
    if (AMOUNT_ONLY_REGEX.test(trimmed)) {
      return { amount: trimmed, unit: undefined };
    }
    return { amount: '', unit: undefined };
  }

  return {
    amount: match[1],
    unit: match[2].toUpperCase() as FileSizeUnit,
  };
};

type Props = {
  value?: string;
  onChange: (value: string) => void;
  onBlur: () => void;
};

const FileSizeField = ({ value, onChange, onBlur }: Props) => {
  const parsed = splitMaxFileSize(value);
  const [amount, setAmount] = useState(parsed.amount);
  const [unit, setUnit] = useState<FileSizeUnit | undefined>(parsed.unit);

  useEffect(() => {
    setAmount(parsed.amount);
    setUnit(parsed.unit);
  }, [value, parsed.amount, parsed.unit]);

  return (
    <Flex sx={{ gap: 2, alignItems: 'stretch' }}>
      <TextField
        value={amount}
        onChange={(e) => {
          const nextAmount = e.target.value;
          setAmount(nextAmount);

          if (!nextAmount) {
            onChange('');
            return;
          }
          if (!unit) return;
          onChange(`${nextAmount}${unit}`);
        }}
        onBlur={onBlur}
        type="number"
        size="large"
        variant="outlined"
        placeholder="Amount"
        sx={{ width: '120px' }}
        inputProps={{ min: 0 }}
      />
      <DropDown
        size="large"
        options={[...FILE_SIZE_UNIT_OPTIONS]}
        value={unit}
        placeholder="Unit"
        sortOptions={false}
        sx={{ width: '100px' }}
        menuWidth="120px"
        onChange={(nextUnit: FileSizeUnit | undefined) => {
          setUnit(nextUnit);

          if (!nextUnit) {
            onChange('');
            return;
          }
          if (!amount) return;
          onChange(`${amount}${nextUnit}`);
        }}
      />
    </Flex>
  );
};

export default FileSizeField;
